// AuthGate — the V2.1 app-boot auth wrapper (P-045 / D-140 + P-046 / D-141
// session context).
//
// Mounts above `<App />` in `main.tsx`. On mount:
//   1. `ensureAnonAuth()` signs the browser into Firebase Anonymous Auth
//      so subsequent Firestore reads pass the `request.auth != null`
//      rule (Tech Arch v2-auth §2.4). Per D-140 sub-decision 3, this
//      lives in the gate's effect — single bootstrap point rather than
//      lazy-init from every Firestore helper.
//   2. `getCurrentSession()` reads the localStorage session flag AND
//      verifies the user still exists in Firestore. Stale sessions
//      (user deleted while logged-in elsewhere) clear themselves.
//
// Render branches:
//   - 'loading' → <LoadingScreen /> (covers the boot-time Firestore round-trip)
//   - null      → <LoginScreen onLogin={refresh} />
//   - {email}   → render children (the existing `<App />`) wrapped in a
//                 SessionContext.Provider so descendants can read the
//                 current user's email without prop-drilling. Settings
//                 (P-046) consumes the context to power its
//                 "cannot remove self" guard.
//
// The auth chunk (Firestore SDK + bcryptjs) loads dynamically inside the
// effect, keeping the initial bundle clean per Tech Arch v2-auth §8.
//
// **Engineering call — Preact context over prop-drilling (P-046 / D-141).**
// AuthGate already owns the authoritative session state; SettingsScreen
// is several levels below it (AuthGate → App → RouterOutlet →
// SettingsScreen). Threading the session as a prop would touch every
// intermediate component; context is the natural fit. A localStorage-
// reading hook was rejected as it bypasses the gate's authoritative
// state machine (would re-read stale data on every render).

import { createContext } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'

import type { Session } from '../lib/auth'
import { LoadingScreen } from './screens/loading'
import { LoginScreen } from './screens/login'

type AuthState = 'loading' | Session | null

/** Context carrying the current user's session, available to every
 *  descendant of `<AuthGate>` after authentication succeeds.
 *
 *  Default value `null` represents "no provider in scope" — callers
 *  use `useCurrentSession()` which throws on `null` rather than
 *  silently returning a missing email. */
const SessionContext = createContext<Session | null>(null)

/** Read the current user's session inside an authenticated subtree.
 *  Throws if called outside `<AuthGate>` — that would mean a render
 *  path bypassed the gate, which is a logic error worth surfacing
 *  loudly rather than masking with a `?? guest` fallback. */
export function useCurrentSession(): Session {
  const session = useContext(SessionContext)
  if (session === null) {
    throw new Error(
      'useCurrentSession called outside <AuthGate>. Wrap the consumer below AuthGate or render it from a screen mounted by RouterOutlet.',
    )
  }
  return session
}

export function AuthGate({ children }: { readonly children: ComponentChildren }) {
  const [state, setState] = useState<AuthState>('loading')

  const refresh = () => {
    setState('loading')
    void resolveSession().then(setState)
  }

  useEffect(() => {
    void resolveSession().then(setState)
  }, [])

  if (state === 'loading') return <LoadingScreen />
  if (state === null) return <LoginScreen onLogin={refresh} />
  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
}

async function resolveSession(): Promise<Session | null> {
  try {
    const [{ ensureAnonAuth }, { getCurrentSession }] = await Promise.all([
      import('../lib/firebase-storage'),
      import('../lib/auth'),
    ])
    await ensureAnonAuth()
    return await getCurrentSession()
  } catch (err) {
    // Catastrophic failures (chunk-load error, Anonymous Auth
    // disabled, etc.) shouldn't strand the gate on the loading
    // screen forever. Route to LoginScreen — the user can retry
    // by submitting credentials, and the surface stays usable
    // even if Firebase is fully down.
    console.error('AuthGate: failed to resolve session', err)
    return null
  }
}
